import { useEffect, useState } from "react";
import {
  AddUpgradePlanDiscountApi,
  DeleteUpgradePlanDiscountApi,
  EditUpgradePlanDiscountApi,
  UpgradePlanDiscountListApi,
} from "../../api/request/upgradePlanDiscount";
import ModalComponent from "../../components/global/modals/ModalComponent";
import SkeletonTable from "../../components/table/SkeletonTable";
import upgradePlansColumns from "../../columns/upgradePlanDiscount/upgradePlanDiscount";
import { Form, InputNumber } from "antd";
import moment from "moment";
import Edit from "../../assets/Edit";
import GlobalTooltip from "../../components/common/GlobalTooltip";
import LoadableButton from "../../components/button/LoadableButton";
import Plus from "../../assets/Plus";
import ConfirmationModal from "../../components/global/modals/ConfirmationModal";
import Delete from "../../assets/Delete";
import toast from "react-hot-toast";

const UpgradePlanDiscount = () => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [form] = Form.useForm();

  const [selectedPlan, setSelectedPlan] = useState(null);

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);

  const fetchPlans = async () => {
    try {
      setIsLoading(true);
      const { data } = await UpgradePlanDiscountListApi();
      setData(
        data?.data?.map((item) => {
          return {
            ...item,
            created_at: item?.created_at
              ? moment(item?.created_at).format("lll")
              : "--",
          };
        })
      );
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const handleSubmit = async (values) => {
    setIsLoading(true);
    try {
      const payload = {
        ...values,
      };

      if (editingPlan) {
        await EditUpgradePlanDiscountApi({ id: editingPlan?.id, ...payload });
      } else {
        await AddUpgradePlanDiscountApi(payload);
      }

      fetchPlans();
      setIsModalOpen(false);
      form.resetFields();
      setEditingPlan(null);
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedPlan) return;
    setIsDeleteLoading(true);
    try {
      await DeleteUpgradePlanDiscountApi({ id: selectedPlan?.id });
      fetchPlans();
      setIsDeleteOpen(false);
      setSelectedPlan(null);
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const openAddModal = () => {
    setEditingPlan(null);
    form.resetFields();
    setIsModalOpen(true);
  };

  const openEditModal = (plan) => {
    setEditingPlan(plan);
    form.setFieldsValue({
      month: plan?.month,
      percentage: plan?.percentage,
    });
    setIsModalOpen(true);
  };

  return (
    <div>
      <div className="border bg-white rounded-xl py-3">
        <div className="flex items-center justify-between sm:px-4 max-sm:px-2 mb-1">
          <h2 className="text-xl font-semibold">Upgrade Plans</h2>
          <button
            className="flex items-center gap-2 m-2 px-4 py-2 bg-secondary text-white rounded-md max-sm:px-1 max-sm:py-1"
            onClick={openAddModal}
          >
            <Plus className="w-5 h-5" />
            <span className="max-sm:text-xs">Add Upgrade Plan</span>
          </button>
        </div>

        <SkeletonTable
          columns={[
            ...upgradePlansColumns,
            {
              title: "Actions",
              key: "actions",
              render: (_, record) => (
                <div className="flex gap-2">
                  <GlobalTooltip title="Edit Upgrade Plan">
                    <button onClick={() => openEditModal(record)}>
                      <Edit />
                    </button>
                  </GlobalTooltip>
                  <GlobalTooltip title="Delete Upgrade Plan">
                    <button
                      disabled={isDeleteLoading}
                      onClick={() => {
                        setSelectedPlan(record);
                        setIsDeleteOpen(true);
                      }}
                    >
                      <Delete />
                    </button>
                  </GlobalTooltip>
                </div>
              ),
            },
          ]}
          data={data}
          isLoading={isLoading}
          pagination={true}
        />
      </div>

      <ModalComponent
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        title={editingPlan ? "Edit Upgrade Plan" : "Add Upgrade Plan"}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ status: true }}
        >
          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              label="Month"
              name="month"
              rules={[{ required: true, message: "Please enter month" }]}
            >
              <InputNumber
                min={0}
                className="w-full"
                placeholder="Enter month"
              />
            </Form.Item>

            <Form.Item
              label="Discount Percentage"
              name="percentage"
              rules={[
                { required: true, message: "Please enter discount percentage" },
                {
                  type: "number",
                  max: 100,
                  message: "Discount percentage cannot be more than 100",
                },
                {
                  type: "number",
                  min: 0,
                  message: "Discount percentage cannot be less than 0",
                },
              ]}
            >
              <InputNumber
                className="w-full"
                placeholder="Enter discount percentage"
              />
            </Form.Item>
          </div>

          <div className="flex justify-center gap-2 mt-4">
            <LoadableButton
              className="btn-primary"
              type="submit"
              lable={editingPlan ? "Update" : "Add"}
              loadingLable="Saving..."
              isLoading={isLoading}
            />
          </div>
        </Form>
      </ModalComponent>

      <ConfirmationModal
        isOpen={isDeleteOpen}
        setIsOpen={setIsDeleteOpen}
        type="error"
        message={`Are you sure you want to delete this plan discount?`}
        onConfirm={handleConfirmDelete}
        isLoading={isDeleteLoading}
        loadingLabel="Deleting..."
      />
    </div>
  );
};

export default UpgradePlanDiscount;
